export enum OpenBankingProvider {
  GOCARDLESS = 'gocardless',
  UNIVERSAL_SANDBOX = 'universal-sandbox',
  NATWEST_PAYIT = 'natwest-payit',
  YAPILY = 'yapily',
  BOTTOMLINE = 'bottomline',
}

// Only GoCardless implements VRP mandates today (IPisp.createMandate/cancelMandate) - add a
// provider here once a second one does. This is deliberately a small allow-list, not derived
// from OpenBankingProvider itself, since most providers in that enum don't support VRP at all.
export const VRP_SUPPORTED_OPEN_BANKING_PROVIDERS: OpenBankingProvider[] = [OpenBankingProvider.GOCARDLESS];

// Mirrors AccountProviderService (config.type.ts) structurally rather than importing it -
// config.type.ts already imports OpenBankingProvider from this file, so importing back would
// be circular. openBankingConfig genuinely lives *inside* account_service, not beside it -
// CommonService.getOpenBankingProvider's own `providerDetails` parameter is already typed as
// AccountProviderService (i.e. the account_service sub-object itself), which is why its field
// access looks flat; a caller holding the whole ProjectConfiguration needs the account_service
// prefix.
type ProjectConfigProviderFields = {
  account_service?: {
    provider:
      | 'integratedFinance'
      | 'modulr'
      | 'railsr'
      | 'andaria'
      | 'universal-sandbox'
      | 'thisbank'
      | 'saascada'
      | 'equalsmoney'
      | 'airwallex'
      | 'keelmoney';
    openBankingConfig?: { provider: OpenBankingProvider; enabled?: boolean; vrpEnabled?: boolean };
  };
};

/**
 * The open-banking/PISP provider actually in effect for a project - mirrors
 * CommonService.getOpenBankingProvider (account-service) exactly: openBankingConfig.provider
 * wins if set, otherwise it defaults to GoCardless unless account_service.provider is
 * universal-sandbox. Most projects never set openBankingConfig, so this resolves to GoCardless
 * far more often than a naive `account_service.provider === 'gocardless'` check would catch.
 */
export function resolveOpenBankingProvider(projectConfig: ProjectConfigProviderFields): OpenBankingProvider {
  return (
    projectConfig.account_service?.openBankingConfig?.provider ??
    (projectConfig.account_service?.provider === 'universal-sandbox'
      ? OpenBankingProvider.UNIVERSAL_SANDBOX
      : OpenBankingProvider.GOCARDLESS)
  );
}

export function providerSupportsVrp(projectConfig: ProjectConfigProviderFields): boolean {
  const config = projectConfig.account_service?.openBankingConfig;
  return (
    config?.enabled !== false &&
    config?.vrpEnabled !== false &&
    VRP_SUPPORTED_OPEN_BANKING_PROVIDERS.includes(resolveOpenBankingProvider(projectConfig))
  );
}
